import dayjs, { Dayjs } from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import {
  OpeningHoursClosedFlags,
  OpeningHoursWithOpeningTimes,
  getFirstOpeningTimeOn,
  isStoreClosedAllDayOn,
} from "../store/opening-hours"

dayjs.extend(utc)
dayjs.extend(timezone)

const STOCKHOLM_TZ = "Europe/Stockholm"

/** Automatic reminders after the first "cykeln är klar" SMS. */
export const MAX_AUTOMATIC_PICKUP_REMINDERS = 8

/** The first reminder goes out on this open day after the bike was finished. */
const OPEN_DAYS_BEFORE_FIRST_REMINDER = 2

/** Later reminders: counted from the day the previous one actually went out. */
const CALENDAR_DAYS_BETWEEN_REMINDERS = 2

/** No automatic reminder goes out before this hour, Stockholm time. */
export const REMINDER_EARLIEST_HOUR = 9

/** No automatic reminder goes out from this hour, Stockholm time. */
export const REMINDER_END_HOUR = 18

/**
 * Pickup reminders go out this long after the store opens. A day's send, not
 * an all-day one: ending a pause in the afternoon doesn't send anything until
 * the next open day.
 */
export const PICKUP_REMINDER_SEND_WINDOW_MINUTES = 30

/**
 * How far ahead to look for an open day. A store marked closed every day would
 * otherwise loop forever; a month of closed days means "don't remind".
 */
const MAX_DAYS_TO_SEARCH = 31

// Days are anchored at noon Stockholm time so adding whole days never lands on
// the wrong calendar date across a daylight-saving switch.
const toStockholmDay = (date: Date): Dayjs =>
  dayjs(date).tz(STOCKHOLM_TZ).startOf("day").add(12, "hour")

const toDayKey = (day: Dayjs): string =>
  day.tz(STOCKHOLM_TZ).format("YYYY-MM-DD")

/**
 * Whether reminders may go out on the given day. Follows the store's opening
 * hours, including "avvikande öppettider" such as a holiday closure. A store
 * with no opening hours on file is treated as open Monday to Friday.
 */
export const isPickupReminderDay = (
  openingHours: OpeningHoursClosedFlags | null,
  date: Date,
): boolean => {
  if (!openingHours) {
    const weekDay = dayjs(date).tz(STOCKHOLM_TZ).day()
    return weekDay !== 0 && weekDay !== 6
  }
  return !isStoreClosedAllDayOn(openingHours, date)
}

/**
 * When reminders may start on the given day: when the store opens, or 09:00 if
 * it opens earlier or no opening time is on file.
 */
export const getReminderStartAt = (
  openingHours: OpeningHoursWithOpeningTimes | null,
  date: Date,
): Date => {
  const opensAt = getFirstOpeningTimeOn(openingHours, date)
  const minuteOfDay = Math.max(
    REMINDER_EARLIEST_HOUR * 60,
    opensAt ? opensAt.hours * 60 + opensAt.minutes : 0,
  )
  const day = dayjs(date).tz(STOCKHOLM_TZ).format("YYYY-MM-DD")
  const hours = String(Math.floor(minuteOfDay / 60)).padStart(2, "0")
  const minutes = String(minuteOfDay % 60).padStart(2, "0")
  // Parsed rather than set on a tz date, which would keep the day's first UTC
  // offset across a daylight-saving switch.
  return dayjs.tz(`${day} ${hours}:${minutes}`, STOCKHOLM_TZ).toDate()
}

/**
 * Whether a reminder can go out on the given day at all: an open day where the
 * store doesn't open too late for it, such as evening hours on a special day.
 */
export const isReminderSendDay = (
  openingHours: OpeningHoursWithOpeningTimes | null,
  date: Date,
): boolean => {
  const startHour = dayjs(getReminderStartAt(openingHours, date))
    .tz(STOCKHOLM_TZ)
    .hour()
  return (
    isPickupReminderDay(openingHours, date) && startHour < REMINDER_END_HOUR
  )
}

const getPickupSendWindowEnd = (
  openingHours: OpeningHoursWithOpeningTimes | null,
  date: Date,
): Date =>
  dayjs(getReminderStartAt(openingHours, date))
    .add(PICKUP_REMINDER_SEND_WINDOW_MINUTES, "minute")
    .toDate()

const findOpenDay = (
  openingHours: OpeningHoursWithOpeningTimes | null,
  from: Dayjs,
  nth: number,
  counts: (
    openingHours: OpeningHoursWithOpeningTimes | null,
    date: Date,
  ) => boolean = isPickupReminderDay,
): Dayjs | null => {
  let found = 0
  for (let offset = 0; offset < MAX_DAYS_TO_SEARCH; offset++) {
    const day = from.add(offset, "day")
    if (counts(openingHours, day.toDate())) {
      found++
      if (found === nth) return day
    }
  }
  return null
}

export interface PickupReminderState {
  /** When the job became Färdig. */
  readyAt: Date
  /** Automatic reminders sent since then. */
  reminderCount: number
  lastReminderAt: Date | null
  openingHours: OpeningHoursWithOpeningTimes | null
}

const laterDay = (a: Dayjs, b: Dayjs): Dayjs =>
  toDayKey(a) >= toDayKey(b) ? a : b

/**
 * The day the next automatic reminder is due, or null if the store has no open
 * day in sight. The first one is the 2nd open day after the bike was finished.
 * After that it is 2 calendar days after the previous reminder, moved forward
 * to the next open day — so a reminder pushed from Sunday to Monday is
 * followed by Wednesday, not Tuesday. A manual "Påminn kund" also counts as
 * the previous reminder, so it can push the first automatic one later.
 */
export const getNextPickupReminderDueDay = ({
  readyAt,
  reminderCount,
  lastReminderAt,
  openingHours,
}: PickupReminderState): Date | null => {
  const afterLastReminder = lastReminderAt
    ? toStockholmDay(lastReminderAt).add(CALENDAR_DAYS_BETWEEN_REMINDERS, "day")
    : null

  if (reminderCount > 0 && afterLastReminder) {
    return findOpenDay(openingHours, afterLastReminder, 1)?.toDate() ?? null
  }

  const dayAfterReady = toStockholmDay(readyAt).add(1, "day")
  const firstReminderDay = findOpenDay(
    openingHours,
    dayAfterReady,
    OPEN_DAYS_BEFORE_FIRST_REMINDER,
  )
  if (!firstReminderDay || !afterLastReminder) {
    return firstReminderDay?.toDate() ?? null
  }

  return (
    findOpenDay(
      openingHours,
      laterDay(firstReminderDay, afterLastReminder),
      1,
    )?.toDate() ?? null
  )
}

/**
 * Whether a mechanic has paused the reminders on `now`'s day. The pause
 * covers the chosen day itself; reminders resume the day after.
 */
export const isPickupReminderPaused = (
  pausedUntil: Date | null,
  now: Date = new Date(),
): boolean =>
  pausedUntil !== null &&
  toDayKey(toStockholmDay(now)) <= toDayKey(toStockholmDay(pausedUntil))

/**
 * Whether the run at `now` should act on this job: send the next reminder, or
 * — once all of them are used up — flag the job to the workshop. Everything
 * happens just after the store opens. Nothing goes out on a closed day or
 * while a mechanic has paused the job; a missed day is caught up with a single
 * reminder, not several.
 */
export const isPickupReminderDue = (
  state: PickupReminderState & { pausedUntil: Date | null },
  now: Date,
): boolean => {
  if (!isReminderSendDay(state.openingHours, now)) return false

  if (
    now < getReminderStartAt(state.openingHours, now) ||
    now >= getPickupSendWindowEnd(state.openingHours, now)
  ) {
    return false
  }

  if (isPickupReminderPaused(state.pausedUntil, now)) return false

  const dueDay = getNextPickupReminderDueDay(state)
  if (!dueDay) return false

  return toDayKey(dayjs(dueDay)) <= toDayKey(toStockholmDay(now))
}

/**
 * The open days a finished bike has been charged parking for by `now`'s day,
 * today included. The day it became Färdig is free, and so is the whole first
 * open day after it; every open day after that counts.
 */
export const getPickupParkingFeeDays = (
  {
    readyAt,
    openingHours,
  }: Pick<PickupReminderState, "readyAt" | "openingHours">,
  now: Date,
): number => {
  const freeDay = findOpenDay(
    openingHours,
    toStockholmDay(readyAt).add(1, "day"),
    1,
  )
  if (!freeDay) return 0

  const todayKey = toDayKey(toStockholmDay(now))
  let chargedDays = 0
  for (
    let day = freeDay.add(1, "day");
    toDayKey(day) <= todayKey;
    day = day.add(1, "day")
  ) {
    if (isPickupReminderDay(openingHours, day.toDate())) chargedDays++
  }
  return chargedDays
}

export interface UpcomingPickupReminders {
  /** The days the remaining automatic reminders are expected to go out. */
  reminderDays: Date[]
  /**
   * The day the workshop is emailed to contact the customer, once all
   * reminders are used up. Null when that is not in sight.
   */
  limitDay: Date | null
}

/**
 * Projects the rest of the reminder sequence from today, as the runs would
 * play it out if nothing changes: no reminder before today's send (or
 * tomorrow's, once today's has passed), none during a pause, and only on days
 * a reminder can go out.
 */
export const getUpcomingPickupReminders = (
  state: PickupReminderState & { pausedUntil: Date | null },
  now: Date,
): UpcomingPickupReminders => {
  const today = toStockholmDay(now)
  let earliest =
    now >= getPickupSendWindowEnd(state.openingHours, now)
      ? today.add(1, "day")
      : today
  if (state.pausedUntil && isPickupReminderPaused(state.pausedUntil, now)) {
    earliest = laterDay(
      earliest,
      toStockholmDay(state.pausedUntil).add(1, "day"),
    )
  }

  const reminderDays: Date[] = []
  let reminderCount = state.reminderCount
  let lastReminderAt = state.lastReminderAt

  while (reminderCount <= MAX_AUTOMATIC_PICKUP_REMINDERS) {
    const dueDay = getNextPickupReminderDueDay({
      ...state,
      reminderCount,
      lastReminderAt,
    })
    if (!dueDay) break

    const day = findOpenDay(
      state.openingHours,
      laterDay(toStockholmDay(dueDay), earliest),
      1,
      isReminderSendDay,
    )
    if (!day) break

    if (reminderCount === MAX_AUTOMATIC_PICKUP_REMINDERS) {
      return { reminderDays, limitDay: day.toDate() }
    }

    reminderDays.push(day.toDate())
    reminderCount++
    lastReminderAt = day.toDate()
  }

  return { reminderDays, limitDay: null }
}
